import { ThresholdSettings } from "model/Threshold";

interface PESDefaults {
  max: number;
  min: number;
  step: number;
  value: number;
}

interface PESScheduleResponse {
  defaults: {
    eventDuration: PESDefaults,
    eventVolume: PESDefaults,
    flowRate: PESDefaults,
    flowRateDuration: PESDefaults,
  },
  hardwareThresholds: {
    tempF: HardwareThresholds,
    tempC: HardwareThresholds,
    humidity: HardwareThresholds,
    battery: HardwareThresholds,
    tempEnabled: boolean,
    humidityEnabled: boolean,
    batteryEnabled: boolean
  }
  items: PESItem[];
}

interface HardwareThresholds{
  okMin: number;
  okMax: number;
  minValue: number;
  maxValue: number;
  value: number;
}

interface PESEventLimits {
  duration: number;
  volume: number;
  flowRate: number;
  flowRateDuration: number;
}

interface PESItem {
  eventLimits: PESEventLimits;
  name: string;
  mode?: string;
  shutoffDisabled?: boolean;
}

export interface PESResponse {
  schedule: PESScheduleResponse;
}

export interface PESPayload {
  duration: number;
  volume: number;
  flowRate: number;
  flowRateDuration?: number;
  shutoffDisabled?: boolean;
}

const flowRateDefault = {
  max: 25,
  min: 0.1,
  step: 0.1,
  value: 0.1
}

const durationDefault = {
  max: 120,
  min: 1,
  step: 1,
  value: 1
}

const volumeDefault = {
  max: 1000,
  min: 1,
  step: 1,
  value: 1
}

const flowRateDurationDefault = {
  max: 60,
  min: 1,
  step: 1,
  value: 1
}

export const toThresholdSettings = (data: PESResponse | null): ThresholdSettings => ({
  duration: {
    value: (data?.schedule.items || []).length > 0 ? Math.round((data?.schedule.items || [])[0].eventLimits.duration / 60) : (data?.schedule.defaults.eventDuration.min || (durationDefault.min * 60)) / 60,
    defaults: data?.schedule.defaults.eventDuration ? {
      max: data?.schedule.defaults.eventDuration.max / 60,
      min: data?.schedule.defaults.eventDuration.min / 60,
      step: data?.schedule.defaults.eventDuration.step / 60,
      value: data?.schedule.defaults.eventDuration.value / 60,
    } : durationDefault
  },
  volume: {
    value: (data?.schedule.items || []).length > 0 ? (data?.schedule.items || [])[0].eventLimits.volume : data?.schedule.defaults.eventVolume.min || volumeDefault.min,
    defaults: data?.schedule.defaults.eventVolume || volumeDefault
  },
  flowRate: {
    value: (data?.schedule.items || []).length > 0 ? (data?.schedule.items || [])[0].eventLimits.flowRate : flowRateDefault.max,
    defaults: data?.schedule.defaults.flowRate || flowRateDefault
  },
  flowRateDuration: {
    value: (data?.schedule.items || []).length > 0 ? (data?.schedule.items || [])[0].eventLimits.flowRateDuration : data?.schedule.defaults.flowRateDuration.min || flowRateDurationDefault.min,
    defaults: data?.schedule.defaults.flowRateDuration || flowRateDurationDefault
  },
  shutoffDisabled: (data?.schedule.items || []).length > 0 ? ((data?.schedule.items || [])[0].shutoffDisabled ?? false) : false,
  batteryEnabled: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.batteryEnabled : false,
  tempEnabled: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.tempEnabled : false,
  humidityEnabled: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.humidityEnabled : false,
  battery: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.battery : {maxValue: 100, minValue: 0, okMin: 40, okMax: 80} as HardwareThresholds,
  humidity: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.humidity : {maxValue: 100, minValue: 0, okMin: 25, okMax: 80} as HardwareThresholds,
  tempC: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.tempC : {maxValue: 40, minValue: 0, okMin: 10, okMax: 30} as HardwareThresholds,
  tempF: data?.schedule.hardwareThresholds ? data?.schedule.hardwareThresholds.tempF : {maxValue: 100, minValue: 0, okMin: 40, okMax: 80} as HardwareThresholds,
})
